import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-4" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "flex items-center gap-4 flex-1" }
const _hoisted_4 = { class: "w-[240px]" }
const _hoisted_5 = { class: "flex items-center gap-2" }
const _hoisted_6 = { class: "flex items-center gap-2 shrink-0" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("p", { class: "text-primary30 text-sm font-bold whitespace-nowrap" }, "Ordenar por:", -1)
const _hoisted_8 = { class: "relative" }

import { ref, computed, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue'
import { mdiChevronDown } from '@mdi/js'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import SortOptionsModal from '@/components/Filters/SortOptionsModal.vue'
import { BIOMETRY_STATUS_FILTER_OPTIONS } from '@/constants/facial-biometry.constants'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { useErrorToast } from '@/composables/useErrorToast'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'


export default /*#__PURE__*/_defineComponent({
  __name: 'FacialBiometryFilterBar',
  emits: ["search", "filterStatus", "filterDate", "filterSchedule", "filterUnit", "sort"],
  setup(__props, { expose: __expose, emit }) {

const statusIcon = defineAsyncComponent(() => import('@/assets/icons/status.vue'))
const scheduleIcon = defineAsyncComponent(() => import('@/assets/icons/scale.vue'))
const hospitalIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))



const scheduleStore = useScheduleStore()
const { notifyError } = useErrorToast()
const healthUnitStore = useHealthUnitStore()

const searchText = ref('')
const sortOrder = ref('ASC')
const isSortModalOpen = ref(false)
const openFilterName = ref<string | null>(null)
const dateFilterRef = ref<InstanceType<typeof DateFilter> | null>(null)
const selectedStatus = ref<string | null>(null)
const selectedSchedule = ref<string | null>(null)
const selectedUnit = ref<string | null>(null)

const statusOptions = BIOMETRY_STATUS_FILTER_OPTIONS.map((o) => ({
  label: o.name,
  value: o.id
}))

// `listSchedules` (GET /schedules) é a lista plana de escalas. `schedulesForPage`
// (GET /schedules/list) é a árvore empresa → unidade → escala, cujo nível de topo
// é a EMPRESA — este filtro mostrava nomes de empresa e filtrava por company.id.
const scheduleOptions = computed(() =>
  scheduleStore.listSchedules.map((s) => ({
    label: s.title,
    value: s.value
  }))
)

const unitOptions = computed(() =>
  (healthUnitStore.healthUnits?.data ?? []).map((u) => ({
    label: u.name,
    value: String(u.id)
  }))
)

const sortModalOptions = [
  { label: 'Mais antigo', value: 'ASC', direction: 'asc' as const, group: 'date' },
  { label: 'Mais recente', value: 'DESC', direction: 'desc' as const, group: 'date' }
]

const currentSortLabel = computed(
  () => sortModalOptions.find((o) => o.value === sortOrder.value)?.label ?? 'Mais antigo'
)

function handleClickOutside() {
  openFilterName.value = null
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  scheduleStore
    .getAllSchedules()
    .catch((error) => notifyError(error, 'Não foi possível carregar as escalas'))
  healthUnitStore.fetchHealthUnits()
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  if (searchDebounce) clearTimeout(searchDebounce)
})

function toggleFilter(name: string) {
  if (name !== 'date') dateFilterRef.value?.close?.()
  openFilterName.value = openFilterName.value === name ? null : name
}

// Limpa o estado visual da barra sem emitir eventos — quem chama (Index) já
// reseta a store e refaz o fetch. Substitui o antigo remount por :key, que
// refazia as requests de escalas/unidades a cada "Limpar filtros".
function reset() {
  searchText.value = ''
  sortOrder.value = 'ASC'
  openFilterName.value = null
  selectedStatus.value = null
  selectedSchedule.value = null
  selectedUnit.value = null
  dateFilterRef.value?.reset?.()
}

__expose({ reset })

let searchDebounce: ReturnType<typeof setTimeout> | null = null

function handleSearch() {
  if (searchDebounce) clearTimeout(searchDebounce)
  searchDebounce = setTimeout(() => emit('search', searchText.value), 400)
}

function handleStatusSelect(value: string) {
  selectedStatus.value = value
  emit('filterStatus', value)
}

function handleDateSelect(payload: { start: Date | null; end: Date | null }) {
  emit('filterDate', payload)
}

function handleScheduleSelect(value: string) {
  selectedSchedule.value = value
  emit('filterSchedule', value)
}

function handleUnitSelect(value: string) {
  selectedUnit.value = value
  emit('filterUnit', value)
}

function handleSortSelect(value: string) {
  sortOrder.value = value
  isSortModalOpen.value = false
  emit('sort', value)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(SearchInput, {
            modelValue: searchText.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
            "data-testid": "biometry-search-input",
            label: "Buscar",
            width: "100%",
            "hide-details": "",
            density: "compact",
            onInput: handleSearch
          }, null, 8, ["modelValue"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createElementVNode("div", {
            class: "relative",
            onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (toggleFilter('status')), ["stop"]))
          }, [
            _createVNode(SelectFilter, {
              "data-testid": "biometry-status-filter",
              label: "Status",
              "is-open": openFilterName.value === 'status',
              options: _unref(statusOptions),
              icon: _unref(statusIcon),
              "selected-value": selectedStatus.value,
              "dropdown-width": "w-64",
              onSelect: handleStatusSelect
            }, null, 8, ["is-open", "options", "icon", "selected-value"])
          ]),
          _createVNode(DateFilter, {
            ref_key: "dateFilterRef",
            ref: dateFilterRef,
            "data-testid": "biometry-date-filter",
            title: "Período",
            "is-range": true,
            onSelect: handleDateSelect
          }, null, 512),
          _createElementVNode("div", {
            class: "relative",
            onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (toggleFilter('schedule')), ["stop"]))
          }, [
            _createVNode(SelectFilter, {
              "data-testid": "biometry-schedule-filter",
              label: "Escalas",
              "is-open": openFilterName.value === 'schedule',
              options: scheduleOptions.value,
              icon: _unref(scheduleIcon),
              "selected-value": selectedSchedule.value,
              "dropdown-width": "w-64",
              searchable: "",
              onSelect: handleScheduleSelect
            }, null, 8, ["is-open", "options", "icon", "selected-value"])
          ]),
          _createElementVNode("div", {
            class: "relative",
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (toggleFilter('unit')), ["stop"]))
          }, [
            _createVNode(SelectFilter, {
              "data-testid": "biometry-unit-filter",
              label: "Unidades",
              "is-open": openFilterName.value === 'unit',
              options: unitOptions.value,
              icon: _unref(hospitalIcon),
              "selected-value": selectedUnit.value,
              "dropdown-width": "w-64",
              searchable: "",
              onSelect: handleUnitSelect
            }, null, 8, ["is-open", "options", "icon", "selected-value"])
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _hoisted_7,
        _createElementVNode("div", _hoisted_8, [
          _createVNode(OutlineButton, {
            "data-testid": "biometry-sort-button",
            text: currentSortLabel.value,
            width: "auto",
            height: "32px",
            "prepend-icon": _unref(mdiChevronDown),
            onClick: _cache[4] || (_cache[4] = ($event: any) => (isSortModalOpen.value = true))
          }, null, 8, ["text", "prepend-icon"]),
          _createVNode(SortOptionsModal, {
            "is-open": isSortModalOpen.value,
            options: sortModalOptions,
            "selected-value": sortOrder.value,
            onSelect: handleSortSelect,
            "onUpdate:isOpen": _cache[5] || (_cache[5] = ($event: any) => (isSortModalOpen.value = $event))
          }, null, 8, ["is-open", "selected-value"])
        ])
      ])
    ])
  ]))
}
}

})